import React, {CSSProperties, useRef, useState} from 'react'

import Colors from '@utils/Colors.js'
import Popover, {PopoverRef} from '../overlays/Popover.js'
import ListItem from '../surfaces/ListItem.js'
import P from '../typography/P.js'
import {buttonResetStyle} from '../buttons/styles.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import type {NavBarBranding, NavBarHistory, NavBarLinkItem} from './types.js'

ensureFocusStyles()

export interface NavBarMenuProps {
    /** The button: icon, title and subTitle (e.g. the user and the account) */
    link: NavBarLinkItem
    /** The menu; falsy entries are skipped */
    links: (NavBarLinkItem | false | null | undefined)[]
    /** Not used, the menu has no active state */
    branding?: NavBarBranding
    history: NavBarHistory
}

/** The account menu at the right of the NavBar: a button with the user's name that opens a menu, right aligned */
export default function NavBarMenu({link, links, history}: NavBarMenuProps) {
    const popoverRef = useRef<PopoverRef>(null)
    const [hover, setHover] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [isMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches)

    const onNavigate = (target: string) => {
        history.push(target)
        popoverRef.current?.close()
    }

    const color = hover ? Colors.buttonSolid : Colors.textDark

    const style: CSSProperties = {
        ...buttonResetStyle,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 16,
        color,
        cursor: 'pointer',
        width: 'auto',
        height: '100%',
        marginLeft: 20
    }

    return (
        <div style={{display: 'flex', justifyContent: 'flex-end', height: '100%', marginLeft: 12}}>
            <Popover
                placement='left'
                ref={popoverRef}
                onToggle={setMenuOpen}
                content={
                    <div>
                        {links.map((link) => {
                            if (link) {
                                return (
                                    <ListItem key={link.title} onClick={link.onClick || (() => onNavigate(link.target))}>
                                        {link.icon && <i style={{marginRight: 12, fontSize: 20}} className={link.icon} />}
                                        <div>{link.title}</div>
                                    </ListItem>
                                )
                            }
                        })}
                    </div>
                }
            >
                {/* The Popover's wrapper opens the menu on the click */}
                <button
                    type='button'
                    className={focusableClass}
                    style={style}
                    aria-haspopup='menu'
                    aria-expanded={menuOpen}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    <div style={{height: '100%', display: 'flex', alignItems: 'center'}}>
                        {link.icon && <i aria-hidden style={{marginRight: 10, fontSize: 20}} className={link.icon} />}
                        <div>
                            <P style={{color}} ellipsis>{link.title}</P>

                            {link.subTitle &&
                                <P style={{color, maxWidth: isMobile ? 100 : ''}} ellipsis> {link.subTitle}</P>
                            }
                        </div>
                    </div>
                    <div style={{height: 4}} />
                </button>
            </Popover>
        </div>
    )
}
